import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, Users, Truck, HardHat, Plus, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { EmployeeTile } from './EmployeeTile';
import { VehicleTile } from './VehicleTile';
import { SubcontractorTile } from './SubcontractorTile';
import { SubcoGroup } from './SubcoGroup';
import { FilterPill } from './FilterPill';

/**
 * Left side dock for the planning week board:
 *   - Tabs: Çalışanlar / Araçlar (with live count badges)
 *   - Search input (employee name OR vehicle plate)
 *   - Per-tab firm filter chips (multi-select Set<id>) + "Ödünç dahil"
 *     checkbox on the employees tab
 *   - Scrollable tile list (drag source for the board)
 *
 * Layout modes:
 *   - mobileLeftOpen=true  → fullscreen on mobile (md:hidden ≥768)
 *   - leftPanelCollapsed=true (desktop only) → renders a thin 36px rail with
 *     icon shortcuts that re-expand on click; the board fills the freed width
 *
 * The collapse preference is owned by the parent (persisted to localStorage).
 */
export function PlanningLeftPanel({
  // tab + search state
  leftTab, setLeftTab,
  search, setSearch,
  // employee firm filter (shared by both tabs)
  empFirmIds, setEmpFirmIds,
  includeBorrowed, setIncludeBorrowed,
  // data
  board,
  filteredEmployees,
  filteredVehicles,
  vehiclesLoaded,
  subcontractors,
  subcoGroups = [],
  subcontractorsLoaded,
  employeeBookedDates,
  employeeWeekSummary,
  dates,
  singleBoardFirmId,
  // actions
  onDragStart,
  onVehicleDragStart,
  onSubcontractorDragStart,
  onAddSubcontractor,
  onDeleteSubcontractor,
  onQuickAssignDay,
  focusedEmpId,
  onToggleFocusEmp,
  // tap-to-pick (mobil atama)
  selectedEmpId,
  onTapEmp,
  // layout state
  mobileLeftOpen,
  leftPanelCollapsed,
  setLeftPanelCollapsed,
}) {
  if (leftPanelCollapsed) {
    // Desktop-only collapsed rail. Mobile (mobileLeftOpen toggle) zaten kendi
    // tam-ekran moduna sahip — rail mobile'da gizli kalır.
    return (
      <div className="hidden md:flex flex-col items-center gap-2 w-9 shrink-0 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg" data-testid="planning-left-panel-rail">
        <button
          type="button"
          onClick={() => setLeftPanelCollapsed(false)}
          className="h-7 w-7 inline-flex items-center justify-center rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:bg-slate-700/40"
          title="Paneli aç"
          data-testid="planning-left-panel-expand"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </button>
        <div className="my-1 h-px w-6 bg-slate-200 dark:bg-slate-700" />
        <button
          type="button"
          onClick={() => { setLeftTab('employees'); setLeftPanelCollapsed(false); }}
          className={`h-7 w-7 inline-flex items-center justify-center rounded ${leftTab === 'employees' ? 'text-slate-900 dark:text-slate-100 bg-slate-100 dark:bg-slate-700/60' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700/40'}`}
          title={`Çalışanlar (${filteredEmployees.length})`}
          data-testid="planning-rail-tab-employees"
        >
          <Users className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => { setLeftTab('vehicles'); setLeftPanelCollapsed(false); }}
          className={`h-7 w-7 inline-flex items-center justify-center rounded ${leftTab === 'vehicles' ? 'text-slate-900 dark:text-slate-100 bg-slate-100 dark:bg-slate-700/60' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700/40'}`}
          title="Araçlar"
          data-testid="planning-rail-tab-vehicles"
        >
          <Truck className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => { setLeftTab('subcontractors'); setLeftPanelCollapsed(false); }}
          className={`h-7 w-7 inline-flex items-center justify-center rounded ${leftTab === 'subcontractors' ? 'text-slate-900 dark:text-slate-100 bg-amber-100 dark:bg-amber-700/40' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700/40'}`}
          title="Taşeronlar"
          data-testid="planning-rail-tab-subcontractors"
        >
          <HardHat className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <Card className={`w-full md:w-72 shrink-0 flex flex-col p-0 overflow-hidden ${mobileLeftOpen ? 'flex' : 'hidden md:flex'}`}>
      <div className="px-2 pt-2 border-b border-slate-200 dark:border-slate-700 flex items-center gap-0.5">
        <button
          onClick={() => setLeftTab('employees')}
          className={`flex-1 min-w-0 inline-flex items-center justify-center gap-1 px-1.5 py-1.5 rounded-t text-xs font-semibold transition-colors ${leftTab === 'employees' ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="planning-left-tab-employees"
        >
          <Users className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">Çalışanlar</span>
          <Badge variant="outline" className="ml-0.5 text-[10px] px-1 shrink-0">{filteredEmployees.length}</Badge>
        </button>
        <button
          onClick={() => setLeftTab('vehicles')}
          className={`flex-1 min-w-0 inline-flex items-center justify-center gap-1 px-1.5 py-1.5 rounded-t text-xs font-semibold transition-colors ${leftTab === 'vehicles' ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="planning-left-tab-vehicles"
        >
          <Truck className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">Araçlar</span>
          {vehiclesLoaded && (
            <Badge variant="outline" className="ml-0.5 text-[10px] px-1 shrink-0">{filteredVehicles.length}</Badge>
          )}
        </button>
        <button
          onClick={() => setLeftTab('subcontractors')}
          className={`flex-1 min-w-0 inline-flex items-center justify-center gap-1 px-1.5 py-1.5 rounded-t text-xs font-semibold transition-colors ${leftTab === 'subcontractors' ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="planning-left-tab-subcontractors"
          title="Taşeronlar (manuel şoför+plaka)"
        >
          <HardHat className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">Taşeron</span>
          {subcontractorsLoaded && (
            <Badge variant="outline" className="ml-0.5 text-[10px] px-1 shrink-0 border-amber-300 text-amber-700">{(subcontractors || []).length + subcoGroups.length}</Badge>
          )}
        </button>
        {/* Desktop-only collapse handle. Mobile zaten kendi toggle'ına sahip. */}
        <button
          type="button"
          onClick={() => setLeftPanelCollapsed(true)}
          className="hidden md:inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700/40"
          title="Paneli daralt"
          data-testid="planning-left-panel-collapse"
        >
          <PanelLeftClose className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="px-2 py-2">
        <div className="relative">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              leftTab === 'vehicles' ? 'Plaka / model ara...' :
              leftTab === 'subcontractors' ? 'Taşeron / plaka ara...' :
              'Ara...'
            }
            className="pl-7 h-8 text-xs"
            data-testid="planning-employee-search"
          />
        </div>
      </div>

      {/* Per-tab firm filter — both tabs share `empFirmIds` Set so switching
          tabs preserves scope. Employees tab additionally has the "Ödünç
          dahil" checkbox to surface borrowed assignments from other firms. */}
      {leftTab === 'employees' && (
        <FirmChipStrip
          label="Çalışan firması"
          board={board}
          empFirmIds={empFirmIds}
          setEmpFirmIds={setEmpFirmIds}
          color="indigo"
          chipPrefix="planning-emp-firm"
          allTestId="planning-emp-firm-all"
          onClearAll={() => { setEmpFirmIds(new Set()); setIncludeBorrowed(false); }}
        >
          {empFirmIds.size > 0 && (
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeBorrowed}
                onChange={(e) => setIncludeBorrowed(e.target.checked)}
                className="h-3 w-3 rounded border-slate-300 text-amber-500 focus:ring-amber-400"
                data-testid="planning-emp-include-borrowed"
              />
              <span>Ödünç dahil (diğer firmalardan)</span>
            </label>
          )}
        </FirmChipStrip>
      )}
      {leftTab === 'vehicles' && (
        <FirmChipStrip
          label="Araç firması"
          board={board}
          empFirmIds={empFirmIds}
          setEmpFirmIds={setEmpFirmIds}
          color="sky"
          chipPrefix="planning-veh-firm"
          allTestId="planning-veh-firm-all"
          onClearAll={() => setEmpFirmIds(new Set())}
        />
      )}

      {leftTab === 'employees' && (
        <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-1">
          {filteredEmployees.map((emp) => {
            const bookedToday = employeeBookedDates.get(emp.id);
            const bookedCount = bookedToday?.size || 0;
            const onLeaveDays = (board?.leave_blocks || []).filter((lb) => lb.user_id === emp.id).length;
            const summary = employeeWeekSummary.get(emp.id) || [];
            return (
              <EmployeeTile
                key={emp.id}
                emp={emp}
                bookedCount={bookedCount}
                onLeaveDays={onLeaveDays}
                summary={summary}
                onDragStart={onDragStart}
                viewingFirmId={singleBoardFirmId}
                weekDates={dates}
                onQuickAssignDay={onQuickAssignDay ? (date) => onQuickAssignDay(date, emp) : null}
                isFocused={focusedEmpId === emp.id}
                onToggleFocus={onToggleFocusEmp}
                isSelected={selectedEmpId === emp.id}
                onTapSelect={onTapEmp}
              />
            );
          })}
          {filteredEmployees.length === 0 && (
            <p className="text-center text-xs text-slate-400 py-4">Çalışan yok</p>
          )}
        </div>
      )}
      {leftTab === 'vehicles' && (
        <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-1" data-testid="planning-vehicles-list">
          {!vehiclesLoaded ? (
            <p className="text-center text-xs text-slate-400 py-4">Araçlar yükleniyor…</p>
          ) : filteredVehicles.length === 0 ? (
            <p className="text-center text-xs text-slate-400 py-4">Araç yok</p>
          ) : (
            filteredVehicles.map((v) => (
              <VehicleTile key={v.id} vehicle={v} onDragStart={onVehicleDragStart} />
            ))
          )}
        </div>
      )}
      {leftTab === 'subcontractors' && (
        <>
          {/* Mart 01, 2026 — "Yeni Taşeron Ekle" butonu kaldırıldı.
              Artık subco ekleme/düzenleme sadece /dashboard/subcos sayfasından
              yapılır (SubcoManagement — tam CRUD, email+şifre girişi,
              işletme adı, firma ataması). */}
          <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-1" data-testid="planning-subcontractors-list">
            {subcoGroups.length > 0 && <p className="px-1 pt-1 text-[10px] font-bold uppercase tracking-wide text-amber-700" data-testid="planning-subco-list-title">Subco Listesi ({subcoGroups.length})</p>}
            {subcoGroups.map((g) => (
              <SubcoGroup key={g.id} group={g} onDragStart={onDragStart} onTapEmp={onTapEmp} selectedEmpId={selectedEmpId} />
            ))}
            {subcoGroups.length > 0 && (subcontractors || []).length > 0 && <p className="px-1 pt-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">Diğer taşeronlar</p>}
            {!subcontractorsLoaded ? (
              <p className="text-center text-xs text-slate-400 py-4">Taşeronlar yükleniyor…</p>
            ) : (subcontractors || []).length === 0 && subcoGroups.length === 0 ? (
              <p className="text-center text-xs text-slate-400 py-4">
                Henüz taşeron eklenmemiş.<br/>
                <span className="text-[10px]">Ayarlar → Subco (Alt Taşeron) sayfasından ekleyin.</span>
              </p>
            ) : (
              (subcontractors || []).map((s) => (
                <SubcontractorTile
                  key={s.id}
                  sub={s}
                  onDragStart={onSubcontractorDragStart}
                  onDelete={onDeleteSubcontractor}
                />
              ))
            )}
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * Reusable firm chip strip (Tümü + per-firm). Both tabs use this with
 * different colors / test-id prefixes; trailing children are rendered below
 * the chips (e.g. the "Ödünç dahil" checkbox on the employees tab).
 */
function FirmChipStrip({ label, board, empFirmIds, setEmpFirmIds, color, chipPrefix, allTestId, onClearAll, children }) {
  return (
    <div className="px-2 pb-2 border-b border-slate-100 dark:border-slate-700">
      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1 mb-1.5">
        <FilterPill active={empFirmIds.size === 0} onClick={onClearAll} testId={allTestId}>Tümü</FilterPill>
        {(board?.firms || []).map((f) => (
          <FilterPill
            key={f.id}
            active={empFirmIds.has(f.id)}
            onClick={() => setEmpFirmIds((prev) => {
              const next = new Set(prev);
              if (next.has(f.id)) next.delete(f.id);
              else next.add(f.id);
              return next;
            })}
            color={color}
            testId={`${chipPrefix}-${f.id}`}
          >
            {f.name}
          </FilterPill>
        ))}
      </div>
      {children}
    </div>
  );
}
